import { useCallback, useEffect, useState } from "react";
import AppLayout from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import ApiTokenCreateDialog from "../components/ApiTokenCreateDialog";
import ApiDocsPanel from "../components/ApiDocsPanel";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Copy, KeyRound } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString();
}

export default function ApiTokensPage() {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [revealedToken, setRevealedToken] = useState(null);
  const { i18n } = useTranslation();
  const isZh = (i18n.resolvedLanguage || i18n.language || "en").startsWith("zh");

  const fetchTokens = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api-tokens");
      setTokens(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load API tokens");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTokens();
  }, [fetchTokens]);

  const handleCreate = async (payload) => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/api-tokens", payload);
      toast.success("Token created");
      setDialogOpen(false);
      setRevealedToken(data.token);
      fetchTokens();
      return data;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to create token");
      return null;
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (t) => {
    if (!window.confirm(`Revoke token "${t.name}"? Any third-party integration using it will stop working immediately.`)) return;
    try {
      await api.delete(`/api-tokens/${t.id}`);
      toast.success("Token revoked");
      fetchTokens();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to revoke token");
    }
  };

  const copyToken = () => {
    navigator.clipboard.writeText(revealedToken);
    toast.success("Copied to clipboard");
  };

  return (
    <AppLayout>
      <div data-testid="api-tokens-page" className="max-w-4xl space-y-6">
        <Tabs defaultValue="tokens" className="space-y-6">
        <TabsList data-testid="api-tokens-tabs" className="bg-gray-100 rounded-md">
          <TabsTrigger value="tokens" data-testid="api-tokens-tab-tokens">{isZh ? "API Token" : "API Tokens"}</TabsTrigger>
          <TabsTrigger value="docs" data-testid="api-tokens-tab-docs">{isZh ? "接口文档" : "API Docs"}</TabsTrigger>
        </TabsList>
        <TabsContent value="docs">
          <ApiDocsPanel />
        </TabsContent>
        <TabsContent value="tokens" className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900">API Tokens</h1>
            <p className="text-sm text-gray-500 mt-1">Manage read-only tokens for third-party inventory integrations.</p>
          </div>
          <Button onClick={() => setDialogOpen(true)} data-testid="add-api-token-button" className="bg-[#1677ff] hover:bg-[#0958d9] transition-colors">
            <Plus size={14} className="mr-1.5" /> New Token
          </Button>
        </div>

        {revealedToken && (
          <div className="bg-amber-50 border border-amber-200 p-4 space-y-2" data-testid="api-token-reveal-banner">
            <p className="text-sm font-medium text-amber-800">Copy this token now - it will not be shown again.</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-white border border-amber-200 px-3 py-2 text-sm font-mono break-all" data-testid="api-token-reveal-value">
                {revealedToken}
              </code>
              <Button size="sm" variant="outline" onClick={copyToken} data-testid="api-token-copy-button" className="border-amber-300">
                <Copy size={14} className="mr-1.5" /> Copy
              </Button>
            </div>
            <button
              data-testid="api-token-reveal-dismiss"
              onClick={() => setRevealedToken(null)}
              className="text-xs text-amber-700 hover:text-amber-900 underline"
            >
              Dismiss
            </button>
          </div>
        )}

        <div className="bg-white border border-gray-200 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent bg-gray-50 border-b border-gray-200">
                <TableHead className="text-[10px] uppercase tracking-label text-gray-500 font-semibold">Name</TableHead>
                <TableHead className="text-[10px] uppercase tracking-label text-gray-500 font-semibold">Token</TableHead>
                <TableHead className="text-[10px] uppercase tracking-label text-gray-500 font-semibold">Created</TableHead>
                <TableHead className="text-[10px] uppercase tracking-label text-gray-500 font-semibold">Expires</TableHead>
                <TableHead className="text-[10px] uppercase tracking-label text-gray-500 font-semibold">Last Used</TableHead>
                <TableHead className="text-[10px] uppercase tracking-label text-gray-500 font-semibold">Status</TableHead>
                <TableHead className="text-[10px] uppercase tracking-label text-gray-500 font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!loading && tokens.map((t) => {
                const isExpired = t.expires_at && new Date(t.expires_at) < new Date();
                const isRevoked = !!t.revoked_at;
                return (
                  <TableRow key={t.id} className="border-b border-gray-50" data-testid={`api-token-row-${t.id}`}>
                    <TableCell className="text-sm text-gray-800">{t.name}</TableCell>
                    <TableCell className="font-mono text-xs text-gray-500">{t.token_prefix}...</TableCell>
                    <TableCell className="text-sm text-gray-500">{formatDate(t.created_at)}</TableCell>
                    <TableCell className="text-sm text-gray-500">{formatDate(t.expires_at)}</TableCell>
                    <TableCell className="text-sm text-gray-500">{t.last_used_at ? formatDate(t.last_used_at) : "Never"}</TableCell>
                    <TableCell>
                      <span
                        data-testid={`api-token-status-${t.id}`}
                        className={`text-xs font-medium px-2 py-0.5 ${
                          isRevoked ? "bg-gray-100 text-gray-500" : isExpired ? "bg-amber-100 text-amber-700" : "bg-emerald-50 text-emerald-600"
                        }`}
                      >
                        {isRevoked ? "Revoked" : isExpired ? "Expired" : "Active"}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {!isRevoked && (
                        <button data-testid={`revoke-api-token-${t.id}`} onClick={() => handleRevoke(t)} className="text-gray-400 hover:text-red-500 transition-colors">
                          <Trash2 size={14} strokeWidth={1.75} />
                        </button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {!loading && tokens.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-sm text-gray-400 py-8" data-testid="api-tokens-empty-state">
                    <KeyRound size={20} className="mx-auto mb-2 text-gray-300" strokeWidth={1.5} />
                    No API tokens yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        </TabsContent>
        </Tabs>
      </div>

      <ApiTokenCreateDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSubmit={handleCreate}
        submitting={submitting}
      />
    </AppLayout>
  );
}
